import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PageLayout from '../layouts/PageLayout';
import CustomCard2 from '../../components/cards/CustomCard2';
import { colors } from '../../assets/utils/colors';
import ProductWidget from '../../components/widgets/ProductWidget';
import BranchDetailsSidebar from '../../components/sidebars/BranchDetailsSidebar';
import { filter, isEmpty, map, reverse, size } from 'lodash';

// RTK query and redux actions imports
import { useGetBranchProductsQuery } from '../../api/branches/branchAPI';
import { setBranchProducts } from '../../features/branchSlice';
import CountBadge from '../../components/CountBadge';
import NoDataFeedback from '../../components/feedbacks/NoDataFeedback';
import { branchesSelector } from '../../features/selectors';
import { getRTKResponseData } from '../../helpers/RTKHelpers';

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 1 }}>{children}</Box>}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        'id': `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

// !############### MAIN FUNC ####################
const BranchDetails = () => {
    // ############### CONST ################
    const dispatch = useDispatch();
    const { branch_id } = useParams();

    // ############## Comp state ##################
    const [value, setValue] = useState(0);
    const [branch, setBranch] = useState({});
    const [onSellProducts, setOnSellProducts] = useState([]);
    const [outOfStockProducts, setOutOfStockProducts] = useState([]);

    // ############## Redux state ##################
    const { branches, branch_products } = useSelector(branchesSelector);

    // ################# RTK ######################
    const { data: products, isSuccess } = useGetBranchProductsQuery(branch_id);

    // ############## FUNC #########################
    // => Get branch details
    const branchDetails = () => {
        const branch = filter(branches, ({ id }) => id === branch_id);
        setBranch(branch[0]);
    };
    // => Get on-sell products
    const getOnSellProducts = () => {
        const onSellProducts = filter(
            branch_products,
            ({ product_details }) => product_details.isOnSale === 1,
        );
        setOnSellProducts(onSellProducts);
    };

    // => Get out of stock products
    const getOutOfStockProducts = () => {
        const outOfStockProducts = filter(
            branch_products,
            ({ product_details }) =>
                product_details.product_quantity -
                    product_details.product_sold ===
                0,
        );
        setOutOfStockProducts(outOfStockProducts);
    };

    // ################## useEffect #####################
    useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed) {
            branchDetails();
            if (isSuccess) {
                dispatch(setBranchProducts(getRTKResponseData(products)));
            }
        }
        return () => {
            isSubscribed = false;
        };
    }, [isSuccess]);

    // let productsCount = 0;
    // let outStockCount = 0;
    // let onSellCount = 0;
    // let outOfStock = [];
    // let onSellProducts = [];

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    // if (isSuccess) {
    //     dispatch(
    //         setBranchProducts({
    //             branch_products: products.data,
    //             productsFound: true,
    //         }),
    //     );
    // }

    // if (size(branch_products)) {
    //     productsCount = size(branch_products);

    //     // -> get out of stock products
    //     outOfStock = filter(
    //         branch_products,
    //         ({ product_details }) =>
    //             product_details.product_quantity -
    //                 product_details.product_sold ===
    //             0,
    //     );

    //     onSellProducts = filter(
    //         branch_products,
    //         ({ product_details }) => product_details.isOnSale === 1,
    //     );

    //     onSellCount = size(onSellProducts);
    //     outStockCount = size(outOfStock);
    // }

    return (
        <>
            <PageLayout
                head="Branch details"
                sidebar={
                    <BranchDetailsSidebar
                        branchInfo={branch}
                        productsCount={size(branch_products)}
                        outStockCount={size(outOfStockProducts)}
                        onSellCount={size(onSellProducts)}
                    />
                }
            >
                <CustomCard2
                    title={'Branch products'}
                    heightClear={640}
                    action={
                        <CountBadge
                            total={size(branch_products)}
                            item={'Products'}
                        />
                    }
                >
                    <Grid container spacing={3} padding={2}>
                        <Box sx={{ width: '100%' }}>
                            <Box
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    position: 'sticky',
                                    top: 0,
                                    background: `linear-gradient(180deg, ${colors.white}, ${colors.white})`,
                                    zIndex: 999,
                                }}
                            >
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="basic tabs example"
                                >
                                    <Tab
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        textTransform:
                                                            'capitalize',
                                                        fontWeight: 'bold',
                                                        mr: 1,
                                                    }}
                                                >
                                                    All
                                                </Typography>
                                                <Chip
                                                    sx={{ fontSize: 10 }}
                                                    label={size(
                                                        branch_products,
                                                    )}
                                                    color="info"
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        {...a11yProps(0)}
                                    />
                                    <Tab
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        textTransform:
                                                            'capitalize',
                                                        fontWeight: 'bold',
                                                        mr: 1,
                                                    }}
                                                >
                                                    On-sell
                                                </Typography>
                                                <Chip
                                                    sx={{ fontSize: 10 }}
                                                    label={size(onSellProducts)}
                                                    color="warning"
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        {...a11yProps(2)}
                                    />
                                    <Tab
                                        label={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent:
                                                        'space-between',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        textTransform:
                                                            'capitalize',
                                                        fontWeight: 'bold',
                                                        mr: 1,
                                                    }}
                                                >
                                                    Out-stock
                                                </Typography>
                                                <Chip
                                                    sx={{ fontSize: 10 }}
                                                    label={size(
                                                        outOfStockProducts,
                                                    )}
                                                    color="error"
                                                    size="small"
                                                />
                                            </Box>
                                        }
                                        {...a11yProps(1)}
                                    />
                                </Tabs>
                            </Box>
                            <TabPanel value={value} index={0}>
                                <Grid container spacing={3} padding={2}>
                                    {isEmpty(branch_products) ? (
                                        <NoDataFeedback message="No product found" />
                                    ) : (
                                        branch_products.map(
                                            (product, index) => (
                                                <Grid item sm={3} key={index}>
                                                    <ProductWidget
                                                        product={
                                                            product.product_details
                                                        }
                                                    />
                                                </Grid>
                                            ),
                                        )
                                    )}
                                </Grid>
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <Grid container spacing={3} padding={2}>
                                    {isEmpty(outOfStockProducts) ? (
                                        <NoDataFeedback message="No product found" />
                                    ) : (
                                        outOfStockProducts.map(
                                            (product, index) => (
                                                <Grid item sm={3} key={index}>
                                                    <ProductWidget
                                                        product={
                                                            product.product_details
                                                        }
                                                    />
                                                </Grid>
                                            ),
                                        )
                                    )}
                                </Grid>
                            </TabPanel>
                            <TabPanel value={value} index={2}>
                                <Grid container spacing={3} padding={2}>
                                    {isEmpty(onSellProducts) ? (
                                        <NoDataFeedback message="No product found" />
                                    ) : (
                                        onSellProducts.map((product, index) => (
                                            <Grid item sm={3} key={index}>
                                                <ProductWidget
                                                    product={
                                                        product.product_details
                                                    }
                                                />
                                            </Grid>
                                        ))
                                    )}
                                </Grid>
                            </TabPanel>
                        </Box>
                    </Grid>
                </CustomCard2>
            </PageLayout>
        </>
    );
};

export default BranchDetails;
